// Student-details CSV for a booking group: the template the office downloads and
// the parse of what comes back. Pure — no network, no DOM — so it's testable and
// the dialog only has to render. Shapes match the backend's StudentCsvFields.
import {
  CSV_SENTINEL_HEADER,
  CSV_SENTINEL_VALUE,
  parseCsv,
  normalizeHeader,
  csvLine,
} from "../../shared utils/csv";

export const STUDENT_ID_HEADER = "student_id";

// Header → Student field, in template order. `kind` drives parsing and the
// template's example row. The three groups mirror the three Student details tabs.
export const STUDENT_CSV_COLUMNS = [
  // Personal
  { header: "name", field: "studentName", kind: "text", example: "Maria" },
  { header: "surname", field: "studentSurname", kind: "text", example: "Rossi" },
  { header: "dob", field: "studentDob", kind: "date", example: "2012-03-14" },
  { header: "gender", field: "studentGender", kind: "text", example: "F" },
  { header: "nationality", field: "studentNationality", kind: "text", example: "Italian" },
  { header: "english_level", field: "englishLevel", kind: "text", example: "B1" },
  // Other info
  { header: "allergies", field: "allergies", kind: "text", example: "nuts" },
  { header: "notes", field: "notes", kind: "text", example: "" },
  { header: "emergency_contact", field: "emergencyContact", kind: "text", example: "+39 333 1234567" },
  { header: "mobile_number", field: "mobileNumber", kind: "text", example: "" },
  { header: "mobile_opt_out", field: "mobilePhoneOptOut", kind: "bool", example: "No" },
  { header: "tshirt_size", field: "tshirtSize", kind: "text", example: "S" },
  // Permissions
  { header: "pool", field: "hasPoolPermission", kind: "bool", example: "Yes" },
  { header: "photo", field: "hasPhotoPermission", kind: "bool", example: "Yes" },
  { header: "medical", field: "hasMedicalPermission", kind: "bool", example: "Yes" },
  { header: "hospital", field: "hasHospitalPermission", kind: "bool", example: "Yes" },
  { header: "excursion", field: "hasExcursionPermission", kind: "bool", example: "Yes" },
  { header: "activity", field: "hasActivityPermission", kind: "bool", example: "Yes" },
  { header: "supervision", field: "hasSupervisionPermission", kind: "bool", example: "No" },
];

export const STUDENT_CSV_HEADERS = [
  CSV_SENTINEL_HEADER,
  STUDENT_ID_HEADER,
  ...STUDENT_CSV_COLUMNS.map((c) => c.header),
];

// The student feed carries most values twice (studentName / student_name); take
// whichever is set.
const pick = (o, ...keys) => {
  for (const k of keys) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return "";
};
const snake = (s) => s.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);

const boolOut = (v) => (v === true ? "Yes" : v === false ? "No" : "");
const dateOut = (v) => (v ? String(v).slice(0, 10) : "");

// How the upload finds each student — in the file itself, because the person
// filling it in (often the agent) never sees the app. Sentinel-marked, so it's
// never imported. One line per sentence so it reads in a spreadsheet cell.
export const TEMPLATE_INSTRUCTIONS = [
  "HOW THIS FILE IS READ: keep the student_id column.",
  "A row WITH a student_id updates that member of the group.",
  "A row WITHOUT one fills the next member whose name is still blank.",
  "Extra rows beyond the group's members can be added as new members on upload.",
  "Blank cells leave the current value alone.",
  "These grey instruction/example rows are never imported - leave them or delete them.",
  "Dates: YYYY-MM-DD or DD/MM/YYYY. Yes/No columns: Yes or No.",
];

// One template row per current member, current values filled in, after the
// instruction row and an example row (both sentinel-marked and skipped on
// import). An empty group gets just those two.
export function buildStudentTemplateCsv(members = []) {
  const instructions = csvLine([
    CSV_SENTINEL_VALUE,
    "",
    TEMPLATE_INSTRUCTIONS.join("\n"),
    ...STUDENT_CSV_COLUMNS.slice(1).map(() => ""),
  ]);
  const example = csvLine([
    CSV_SENTINEL_VALUE,
    "",
    ...STUDENT_CSV_COLUMNS.map((c) => c.example),
  ]);
  const memberLine = (m) =>
    csvLine([
      "",
      pick(m, "id"),
      ...STUDENT_CSV_COLUMNS.map((c) => {
        const v = pick(m, c.field, snake(c.field));
        if (c.kind === "bool") return boolOut(typeof v === "boolean" ? v : v === "" ? null : Boolean(v));
        if (c.kind === "date") return dateOut(v);
        return v;
      }),
    ]);
  return [STUDENT_CSV_HEADERS.join(","), instructions, example, ...members.map(memberLine)].join("\n") + "\n";
}

// "2012-03-14" or "14/03/2012" (what UK/Italian Excel writes) → ISO. null when
// blank, undefined when it's neither — a typo must not read as a date.
export function normaliseDob(v) {
  const s = String(v ?? "").trim();
  if (!s) return null;
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  let y;
  let mo;
  let d;
  if (m) {
    [, y, mo, d] = m;
  } else {
    m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
    if (!m) return undefined;
    [, d, mo, y] = m;
  }
  const yy = Number(y);
  const mm = Number(mo);
  const dd = Number(d);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return undefined;
  const dt = new Date(Date.UTC(yy, mm - 1, dd));
  if (dt.getUTCMonth() !== mm - 1 || dt.getUTCDate() !== dd) return undefined; // 31/02
  return `${yy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
}

// Yes/No/Y/N/TRUE/FALSE/1/0, blank → null (leave unchanged), anything else →
// undefined (a row error, not a silent "No").
export function normaliseBool(v) {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return null;
  if (["yes", "y", "true", "1"].includes(s)) return true;
  if (["no", "n", "false", "0"].includes(s)) return false;
  return undefined;
}

// Parse an uploaded file into the backend's row shape.
//   rows:   [{ row, studentId, fields }]  — row is the 1-based line in the file
//   errors: [{ row, message }]            — rows left out of `rows`
//   skippedExample: count of sentinel rows dropped
// Blank cells become null (leave unchanged). Unknown columns are ignored and
// missing columns are simply blank, so an agent's own headers work as long as
// the names match.
export function parseStudentCsv(text) {
  const raw = parseCsv(String(text || ""));
  if (!raw.length) return { rows: [], errors: [{ row: 0, message: "The file is empty" }], skippedExample: 0 };

  const headers = raw[0].map(normalizeHeader);
  const idx = {};
  headers.forEach((h, i) => {
    if (h && idx[h] === undefined) idx[h] = i;
  });
  const known = STUDENT_CSV_COLUMNS.filter((c) => idx[c.header] !== undefined);
  if (!known.length) {
    return {
      rows: [],
      errors: [{ row: 1, message: `No recognised columns — expected e.g. ${STUDENT_CSV_COLUMNS.slice(0, 4).map((c) => c.header).join(", ")}` }],
      skippedExample: 0,
    };
  }

  const rows = [];
  const errors = [];
  let skippedExample = 0;
  const cell = (cells, header) => {
    const i = idx[header];
    return i === undefined ? "" : String(cells[i] ?? "");
  };

  for (let r = 1; r < raw.length; r++) {
    const cells = raw[r];
    const rowNo = r + 1; // 1-based, header is line 1
    if (cell(cells, CSV_SENTINEL_HEADER).trim().toUpperCase() === CSV_SENTINEL_VALUE) {
      skippedExample += 1;
      continue;
    }

    const idText = cell(cells, STUDENT_ID_HEADER).trim();
    let studentId = null;
    if (idText) {
      if (!/^\d+$/.test(idText)) {
        errors.push({ row: rowNo, message: `student_id "${idText}" is not a number` });
        continue;
      }
      studentId = Number(idText);
    }

    const fields = {};
    let bad = null;
    let anyValue = false;
    for (const c of known) {
      const v = cell(cells, c.header);
      if (c.kind === "date") {
        const d = normaliseDob(v);
        if (d === undefined) {
          bad = `dob "${v.trim()}" is not a date (use YYYY-MM-DD or DD/MM/YYYY)`;
          break;
        }
        fields[c.field] = d;
        if (d !== null) anyValue = true;
      } else if (c.kind === "bool") {
        const b = normaliseBool(v);
        if (b === undefined) {
          bad = `${c.header} "${v.trim()}" should be Yes or No`;
          break;
        }
        fields[c.field] = b;
        if (b !== null) anyValue = true;
      } else {
        const t = v.trim();
        fields[c.field] = t === "" ? null : t;
        if (t !== "") anyValue = true;
      }
    }
    if (bad) {
      errors.push({ row: rowNo, message: bad });
      continue;
    }
    if (!anyValue && studentId == null) continue; // a wholly blank line
    rows.push({ row: rowNo, studentId, fields });
  }

  return { rows, errors, skippedExample };
}

// A short "Maria Rossi" / "row 7" label for the preview and result tables.
export function rowLabel(r) {
  const n = [r?.fields?.studentName, r?.fields?.studentSurname].filter(Boolean).join(" ");
  return n || `row ${r?.row}`;
}
